import {Box} from '@mui/material'
import {useCallback} from 'react'
import {NodeApi, Tree} from 'react-arborist'
import {useParams} from 'react-router'
import useResizeObserver from 'use-resize-observer'

import {
  MaterialTreeNode,
  MaterialTree,
  isExistingMaterialGuard,
  isNewMaterialGuard
} from '../../types'
import {
  isBoughtFromVendorOrBoughtAndProduced,
  isProducedInPlantOrBoughtAndProduced
} from '../materials-tabs/general/MaterialSourceInputs'

import {FilterOptions} from './MaterialsTreeFilters'
import {MaterialsTreeNode} from './MaterialsTreeNode'

import {UrlParams} from '@settings/modules/routing'

interface MaterialsTreeProps {
  treeData: MaterialTree
  treeQuickFilter?: FilterOptions
  treeFilterText?: string
}

export const MaterialsTree: React.FC<MaterialsTreeProps> = ({
  treeData,
  treeQuickFilter,
  treeFilterText
}) => {
  //  material is not available in every route
  const {materialId} = useParams<Partial<UrlParams>>()
  const {ref: treeRef, width, height} = useResizeObserver()

  const treeFilterFunction = useCallback(
    (node: NodeApi<MaterialTreeNode>) => {
      const originalEntity = node.data.originalEntity
      const isExistingMaterial = isExistingMaterialGuard(originalEntity)
      const isNewMaterial = isNewMaterialGuard(originalEntity)
      if ((!treeFilterText && !treeQuickFilter) || isNewMaterial) return true
      if (!isExistingMaterial) return false

      const materialName = originalEntity.name.toLowerCase()
      const globalMaterialName = originalEntity.globalName.toLowerCase()
      const materialSource = originalEntity.source

      const quickFilter =
        !treeQuickFilter || treeQuickFilter === FilterOptions.All
          ? true
          : treeQuickFilter === FilterOptions.Produced
            ? isProducedInPlantOrBoughtAndProduced(materialSource)
            : treeQuickFilter === FilterOptions.Received
              ? isBoughtFromVendorOrBoughtAndProduced(materialSource)
              : false

      const textFilter = treeFilterText
        ? materialName.includes(treeFilterText.toLowerCase()) ||
          (!!globalMaterialName && globalMaterialName.includes(treeFilterText.toLowerCase()))
        : true

      return quickFilter && textFilter
    },
    [treeFilterText, treeQuickFilter]
  )

  const treeSearchTerm =
    treeFilterText ||
    (treeQuickFilter && treeQuickFilter !== FilterOptions.All ? treeQuickFilter : undefined)

  return (
    <Box ref={treeRef} height={'calc(100vh - 510px)'}>
      <Tree
        data={treeData}
        openByDefault={false}
        disableDrag={true}
        disableDrop={true}
        height={height}
        width={width}
        rowHeight={58}
        indent={24}
        searchTerm={treeSearchTerm}
        searchMatch={treeFilterFunction}
        selection={materialId}
      >
        {MaterialsTreeNode}
      </Tree>
    </Box>
  )
}
